import moment from "moment-timezone";
import { Flag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StagedTodo } from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/utils";

export interface StagedTodoCardProps {
  todo: StagedTodo;
  isApproving: boolean;
  onViewTranscript: () => void;
  onManage: () => void;
  onHide: () => void;
  onApprove: () => void;
  /** Show a badge with the todo's review state (needs review / approved / hidden). */
  showStatus?: boolean;
}

export function StagedTodoCard({
  todo,
  isApproving,
  onViewTranscript,
  onManage,
  onHide,
  onApprove,
  showStatus,
}: StagedTodoCardProps) {
  const hasTranscript = !!todo.Call?.has_transcript;
  const canHide = !todo.hidden_date;
  const canApprove = !todo.approved_date && !todo.hidden_date;
  const isLocked = !!(todo.approved_date || todo.hidden_date);

  return (
    <Card className="transition-colors duration-200" data-testid={`staged-todo-${todo.id}`}>
      <CardContent className="flex flex-col p-4">
        <div className="mb-2 flex w-full flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="flex-shrink-0 border-yellow-200 bg-yellow-50 text-yellow-700"
            title="Created automatically from a call recording"
          >
            AI Assistant
          </Badge>
          <span className="text-sm font-semibold text-red-600">
            {todo.Customer?.long_name || "Unknown Customer"}
          </span>
          {showStatus && (
            <Badge variant="outline" data-testid={`staged-todo-status-${todo.id}`}>
              {todo.hidden_date ? "Hidden" : todo.approved_date ? "Approved" : "Needs review"}
            </Badge>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {todo.flagged_date && (
              <Flag className="h-4 w-4 flex-shrink-0 text-red-600" aria-label="Flagged">
                <title>{todo.flagged_reason ? `Flagged: ${todo.flagged_reason}` : "Flagged"}</title>
              </Flag>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`staged-todo-transcript-${todo.id}`}
              onClick={onViewTranscript}
              disabled={!hasTranscript}
              title={hasTranscript ? undefined : "No transcript available"}
            >
              View Transcript
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`staged-todo-manage-${todo.id}`}
              onClick={onManage}
              disabled={isLocked}
              title={isLocked ? "Approved or hidden Todos cannot be managed" : undefined}
            >
              Manage
            </Button>
            {canHide && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                data-testid={`staged-todo-hide-${todo.id}`}
                onClick={onHide}
                disabled={isApproving}
              >
                Hide
              </Button>
            )}
            {canApprove && (
              <Button
                type="button"
                size="sm"
                data-testid={`staged-todo-approve-${todo.id}`}
                onClick={onApprove}
                disabled={isApproving}
              >
                {isApproving ? "Approving..." : "Approve"}
              </Button>
            )}
          </div>
        </div>

        <Textarea
          className="mb-3 border-0 text-sm"
          value={todo.content}
          rows={4}
          readOnly
        />

        <div className="grid grid-cols-2 items-start gap-3 text-xs text-gray-500 md:flex md:flex-row md:items-end md:justify-between md:gap-0">
          <div className="grid">
            <span>Created At</span>
            <time
              className="flex-shrink-0"
              dateTime={moment.tz(todo.created_at, CommonDestinationTimezone).format()}
            >
              {moment.tz(todo.created_at, CommonDestinationTimezone).format("MMM Do YYYY, h:mm A")}
            </time>
          </div>
          <div className="grid">
            <span>Last Updated</span>
            <time
              className="flex-shrink-0"
              dateTime={moment.tz(todo.updated_at, CommonDestinationTimezone).format()}
            >
              {moment.tz(todo.updated_at, CommonDestinationTimezone).format("MMM Do YYYY, h:mm A")}
            </time>
          </div>
          <div className="grid">
            <span>Created By:</span>
            <span className="text-sm text-muted-foreground">
              {todo.CreatedBy?.name || "Unknown"}
            </span>
          </div>
          <div className="grid">
            <span>Last Updated By:</span>
            <span className="text-sm text-muted-foreground">
              {todo.LastUpdatedBy?.name || "Unknown"}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
